import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@utils/cn';

import type { TooltipProps } from './Tooltip.types';

import { tooltipContentClasses, tooltipWrapperClasses } from './Tooltip.styles';

function getDefaultPortalTarget(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.body;
}

export const Tooltip = ({
  content,
  children,
  tabIndex = 0,
  'aria-label': ariaLabel,
  className,
  side = 'top'
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [positionReady, setPositionReady] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(getDefaultPortalTarget);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleSetIsVisible = (bool) => {
    if(content){
      setIsVisible(bool)
    }
  }

  // const tooltipId = id || `tooltip-${Math.random().toString(36)}`;


  // When the trigger is inside a ShadowRoot (e.g. TailwindWrapper), render tooltip inline
  useLayoutEffect(() => {
    if (!wrapperRef.current) return;
    const root = wrapperRef.current.getRootNode();
    if (root instanceof ShadowRoot) {
      setPortalTarget(null);
    }
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) {
      setPositionReady(false);
      return;
    }
    if (!wrapperRef.current || !tooltipRef.current) return;

    const applyPosition = () => {
      if (!wrapperRef.current || !tooltipRef.current) return;
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      if (tooltipRect.width === 0 || tooltipRect.height === 0) return;

      if (side === 'right') {
        setTooltipPosition({
          top: wrapperRect.top + wrapperRect.height / 2 - tooltipRect.height / 2,
          left: wrapperRect.right + 8,
        });
      } else {
        setTooltipPosition({
          top: wrapperRect.top - tooltipRect.height - 8,
          left: wrapperRect.left + wrapperRect.width / 2 - tooltipRect.width / 2,
        });
      }
      setPositionReady(true);
    };

    applyPosition();
    if (tooltipRef.current.getBoundingClientRect().width === 0) {
      const raf = requestAnimationFrame(() => applyPosition());
      return () => cancelAnimationFrame(raf);
    }
  }, [isVisible, side]);

  if(!content){
    return <div ref={wrapperRef} aria-label={ariaLabel} className={cn(tooltipWrapperClasses(), className)}>{children}</div>
  }

  const tooltipNode = (
    <div
      ref={tooltipRef}
      role="tooltip"
      aria-live="polite"
      className={cn(tooltipContentClasses(), `${content ? 'cursor-pointer' : ''}`)}
      style={{
        position: 'fixed',
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        display: isVisible ? 'block' : 'none',
        visibility: isVisible && positionReady ? 'visible' : 'hidden'
      }}
    >
      {content}
    </div>
  );

  return (
    <>
      <div
        ref={wrapperRef}
        role="button"
        tabIndex={tabIndex}
        onMouseEnter={() => setTimeout(() => setIsVisible(true), 200)}
        onMouseLeave={() => setTimeout(() => setIsVisible(false), 200)}
        aria-expanded={isVisible}
        aria-label={ariaLabel}
        className={cn(tooltipWrapperClasses(), className)}
      >
        {children}
      </div>
      {portalTarget ? createPortal(tooltipNode, portalTarget) : tooltipNode}
    </>
  );
};